import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal } from 'reactstrap';
import { createNewWorker } from '../../../actions/workers';
import { getLanguagesList } from '../../../actions';
import { genderList, onboardingStatuses } from '../../../constants/general';
import { appPath } from '../../../constants';
import { modifyObject } from '../../../helpers/modifyObject';
import { visuallyFormatPhoneNumber } from '../../../helpers/general';
import FormActionButtons from '../../common/forms/FormActionButtons';
import Close from '../../common/ui/Close';
import Input from '../../common/forms/Input';
import SwitchRow from '../../common/forms/SwitchRow';
import Validator from '../../common/forms/Validator';
import AddressInputAutoComplete from '../../common/AddressInputAutoComplete';
import Radio from '../../common/forms/Radio';
import ErrorMessage from '../../common/forms/ErrorMessage';

class CreateWorker extends Validator {
  static propTypes = {
    history: PropTypes.object.isRequired,
    createNewWorker: PropTypes.func.isRequired,
    getLanguagesList: PropTypes.func.isRequired,
    languagesList: PropTypes.array.isRequired
  };

  initialState = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    photo_image_url: '',
    gender: null,
    language_id: null,
    secondary_language_id: null,
    has_car: false,
    address: '',
    address2: '',
    state: '',
    city: '',
    zip: '',
    address_coordinates: '',
    password: '',
    temp_password: true
  };

  validationRules = {
    first_name: { required: true },
    last_name: { required: true },
    email: { email: true },
    password: { required: true },
    phone: { required: true },
    address: { required: true },
    language_id: { required: true },
    gender: { required: true }
  };

  state = this.initialState;

  handleChange = event => {
    const { name, value } = event.target;
    const newState = { [name]: value };

    if (name === 'phone') {
      newState[name] = visuallyFormatPhoneNumber(event.target.value);
    }

    this.setState(newState);
  };

  handleRadio = (name, value) => {
    const newState = { [name]: value };

    if (name === 'language_id') {
      newState.secondary_language_id = null;
    }

    this.setState(newState);
  };

  handleSwitch = name => this.setState(prevState => ({ [name]: !prevState[name] }));

  handleAddressChange = addressInfo => this.setState({ ...addressInfo });

  createWorker = event => {
    event.preventDefault();

    if (this.validate(this.validationRules)) {
      const workerData = modifyObject({
        ...this.state,
        onboarding_status: onboardingStatuses.ACCOUNT_VERIFIED
      });

      this.props.createNewWorker(workerData).then(
        ({ data }) => {
          if (data) {
            this.props.history.push(`/${appPath}/workers/${data.principalid}`);
          }
        }
      );
    }
  };

  cancelCreate = () => {
    this.setState(this.initialState);
    this.props.toggleModal();
  };

  componentDidMount () {
    this.props.getLanguagesList();
  }

  render () {
    const { languagesList, isOpen, toggleModal } = this.props;
    const { first_name, last_name, email, password, temp_password, phone, address2, address, city, state, zip, gender, language_id, secondary_language_id, has_car } = this.state;

    const errors = this.validationErrors;

    const secondaryLanguages = languagesList.filter(lang => lang.id !== language_id);

    return (
      <Modal className='create-modal' isOpen={isOpen} toggle={toggleModal}>
        <form onSubmit={this.createWorker}>
          <div className='modal-header'>
            <div className='modal-title'>Create New Worker</div>
            <Close onClick={toggleModal} />
          </div>

          <div className='modal-body'>
            <Input value={first_name} name='first_name' error={errors.first_name} label='First Name' onChange={this.handleChange} />
            <Input value={last_name} name='last_name' error={errors.last_name} label='Last Name' onChange={this.handleChange} />
            <Input value={email} name='email' error={errors.email} label='Email' type='email' onChange={this.handleChange} />
            <Input value={password} name='password' error={errors.password} label='Password' type='password' onChange={this.handleChange} />

            <div className='form-group'>
              <SwitchRow
                label='Force Password Reset'
                checked={temp_password}
                onChange={() => this.handleSwitch('temp_password')}
              />
            </div>

            <Input value={phone} name='phone' error={errors.phone} label='Phone' type='tel' onChange={this.handleChange} />

            <div className='form-group'>
              <AddressInputAutoComplete
                addressInfo={{ address, city, state, zip }}
                handleAddressChange={this.handleAddressChange}
              />
              <ErrorMessage errorText={errors.address} />
            </div>

            <Input value={address2} name='address2' label='Address 2' onChange={this.handleChange} />

            <div className='form-group'>
              <label className='mb-1'>Gender</label>
              <div className='d-flex'>
                {genderList.map(({ code, label }) => (
                  <Radio
                    name='gender'
                    key={code}
                    onChange={() => this.handleRadio('gender', code)}
                    checked={gender === code}
                    value={code}
                    label={label}
                  />
                ))}
              </div>
              <ErrorMessage errorText={errors.gender} />
            </div>

            <div className='form-group'>
              <label className='mb-1'>Primary Language</label>
              <div className='d-flex'>
                {languagesList.map(({ id, language_name, short }) => (
                  <Radio
                    name='language_id'
                    key={short}
                    onChange={() => this.handleRadio('language_id', id)}
                    checked={language_id === id}
                    value={id}
                    label={language_name}
                  />
                ))}
              </div>
              <ErrorMessage errorText={errors.language_id} />
            </div>

            {language_id && (
              <div className='form-group'>
                <label className='mb-1'>Secondary Language</label>
                <div className='d-flex'>
                  {secondaryLanguages.map(({ id, language_name, short }) => (
                    <Radio
                      name='secondary_language_id'
                      key={short}
                      onChange={() => this.handleRadio('secondary_language_id', id)}
                      checked={secondary_language_id === id}
                      value={id}
                      id={short}
                      label={language_name}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className='form-group'>
              <SwitchRow
                label='Has Car'
                checked={has_car}
                onChange={() => this.handleSwitch('has_car')}
              />
            </div>

            <FormActionButtons onClickCancel={this.cancelCreate} submitTitle='Create' />
          </div>
        </form>
      </Modal>
    );
  }
}

const mapStateToProps = state => {
  return {
    languagesList: state.languagesList
  };
};

const mapDispatchToProps = {
  createNewWorker,
  getLanguagesList
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateWorker);



// WEBPACK FOOTER //
// ./src/components/app/createWorker/index.js